import { HttpResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { catchError, combineLatest, distinctUntilChanged, filter, from, map, Observable, switchMap } from 'rxjs';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { IDevice } from '../../models/device';
import { resizeImageTo1920 } from 'src/app/shared/helpers/image-scaling';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';

@Component({
    selector: 'app-device-detail-attachments',
    templateUrl: './device-detail-attachments.component.html',
    styleUrl: './device-detail-attachments.component.scss',
    standalone: false
})
export class DeviceDetailAttachmentsComponent {
  @Input() isAdmin;
  @Input() deviceId;
  
  vm$: Observable<any>;

  // Upload file type restrictions
  restrictions: FileRestrictions = {
    allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf"],
  };

  public documentDeletionDialogOpened = false;
  public imageDeletionDialogOpened = false;
  public selectedItemToDelete: any = null;

  constructor(
    private translocoService: TranslocoService,
    private fb: FormBuilder,
    private attachmentStore: AttachmentStore,
    private attachmentService: AttachmentService,
    private deviceStore: DeviceStore,
  ) { 
    this.vm$ = combineLatest([ 
      this.attachmentStore.documents$,
      this.attachmentStore.images$,
      this.attachmentStore.isLoading$,
      this.deviceStore.device$
    ]).pipe(
      map(([
        documents, 
        images,
        isUploading,
        device
      ]) => ({
        documents, 
        images,
        isUploading,
        device
      }))
    );
  }

  ngOnInit() {
    this.vm$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.images === curr.images)
      )
      .subscribe(vm => {
        vm.images.forEach(image => {
          from(this.attachmentService.waitForThumbnail(image.id, 3, 500)) // shorter timeout
            .pipe(
              switchMap(hasThumbnail => {
                // if thumbnail exists, download midsize
                if (hasThumbnail) {
                  return this.attachmentService.downloadMidsize({ attachmentId: image.id });
                }
                // otherwise, download original
                return this.attachmentService.download({ attachmentId: image.id });
              }),
              catchError(() =>
                this.attachmentService.download({ attachmentId: image.id })
              )
            )
            .subscribe((res: HttpResponse<Blob>) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                image.data = reader.result as string;
              };
              reader.readAsDataURL(res.body);
            });
        });
      });

    this.attachmentStore.getAttachmentsByDevice(this.deviceId);
  }

  public async uploadAttachment(event: UploadEvent, device: IDevice): Promise<void> {
    event.preventDefault();
  
    // Create an array to store all FormData objects
    const formDataArray: FormData[] = [];
  
    // Loop through selected files and create FormData for each
    for (const file of event.files) {
      // Resize image
      const processedBlob = await resizeImageTo1920(file.rawFile);
      const processedFile = new File([processedBlob], file.name, { type: file.rawFile.type });

      const formData = new FormData();
      formData.append('file', processedFile, file.name);
  
      // Append optional IDs if provided
      if (this.deviceId) {
        formData.append('deviceId', device.id);
      }
      formData.append('tenantId', device.company.tenantId);
  
      formDataArray.push(formData);
    }
  
    // Call the new addAttachments effect with all files at once
    if (formDataArray.length > 0) {
      this.attachmentStore.addAttachments(formDataArray);
    }
  }
  

  public downloadAttachment(id: string, filename?: string) {
    const fileNameToUse = filename ?? "unbenannt";
    this.attachmentService.download({attachmentId: id}).subscribe((response) => downloadBlob(response, fileNameToUse));
  }

  public deleteAttachment(id: string) {
    this.attachmentStore.removeAttachmentById(id);
  }

  openInNewTab(blob : Blob) {
      // Create a URL object from the Blob
      const url = window.URL.createObjectURL(blob);

      // Optionally, extract the filename from the response headers (if necessary)
      const filename = 'downloaded-file';  // Fallback filename, you can extract from headers if provided
      const contentDisposition = blob.type;  // Use blob's MIME type or extract filename from content-disposition header if backend sets it

      // Open the file in a new window
      window.open(url);

      // Revoke the object URL after use
      window.URL.revokeObjectURL(url);
  }

  renderImage(id: string): Observable<string> {
    return this.attachmentService.download({ attachmentId: id }).pipe(
      switchMap((blob: HttpResponse<Blob>) => {
        const reader = new FileReader();
        const url$ = new Observable<string>((observer) => {
          reader.onloadend = () => {
            // Emit the image data URL when the reader finishes
            observer.next(reader.result as string);
            observer.complete();
          };
          reader.readAsDataURL(blob.body);
        });
        return url$;
      })
    );
  }
  
  // delete confirmation dialog
  public documentDeletionDialogClose(status: string): void {
    this.documentDeletionDialogOpened = false;

    if(status == 'delete' && this.selectedItemToDelete) {
      this.deleteAttachment(this.selectedItemToDelete);
    }

    this.selectedItemToDelete = null;
  }

  public documentDeletionDialogOpen(id: string): void {
    this.documentDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }
    
  public imageDeletionDialogClose(status: string): void {
    this.imageDeletionDialogOpened = false;

    if(status == 'delete' && this.selectedItemToDelete) {
      this.deleteAttachment(this.selectedItemToDelete);
    }

    this.selectedItemToDelete = null;
  }

  public imageDeletionDialogOpen(id: string): void {
    this.imageDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }
}
